import { useLayoutEffect, useRef, useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowDown01Icon,
  ArrowUp01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { cx } from "shared/utils";
import { Show } from "shared/components";

type TranscriptLine = {
  speaker: string;
  message: string;
};

const DEFAULT_PREVIEW_LINES = 3;

export const CallTranscript = ({
  lines,
  contactName = "Unknown",
  previewLines = DEFAULT_PREVIEW_LINES,
  hideHeader = false,
  speakerClassName = "",
  messageClassName = ""
}: {
  lines: TranscriptLine[];
  contactName?: string;
  previewLines?: number;
  hideHeader?: boolean;
  speakerClassName?: string;
  messageClassName?: string;
}) => {
  const [expanded, setExpanded] = useState(false);
  // A collapsed transcript clamps every visible line to a single line, so a
  // long line is cut even when the transcript is short (<= previewLines). Track
  // whether any visible line is actually clipped so the expander still appears
  // and the full text is always reachable — not only when there are extra rows.
  const [hasClippedLine, setHasClippedLine] = useState(false);
  const messageRefs = useRef<(HTMLSpanElement | null)[]>([]);

  const hasMoreLines = lines.length > previewLines;

  useLayoutEffect(() => {
    if (expanded) return;
    const clipped = messageRefs.current.some(
      (el) => el != null && el.scrollWidth > el.clientWidth
    );
    setHasClippedLine(clipped);
  }, [expanded, lines, contactName, previewLines]);

  if (lines.length === 0) return null;

  const visibleLines = expanded ? lines : lines.slice(0, previewLines);
  const canToggle = hasMoreLines || hasClippedLine;

  return (
    <>
      <div>
        <Show if={!hideHeader}>
          <p className="mb-1 text-xs font-medium text-content-200">
            Transcript
          </p>
        </Show>
        <div className="space-y-1">
          {visibleLines.map((line, i) => {
            const speaker =
              line.speaker.toLowerCase() === "agent"
                ? "Agent"
                : contactName || line.speaker;
            return (
              <div key={i} className="flex gap-2 text-sm text-content-1000">
                <span
                  className={cx(
                    "font-medium shrink-0 w-[100px] text-content-300",
                    speakerClassName
                  )}
                >
                  {speaker}:
                </span>
                <span
                  ref={(el) => {
                    messageRefs.current[i] = el;
                  }}
                  className={cx(
                    "min-w-0 flex-1",
                    expanded ? "whitespace-pre-wrap break-words" : "truncate",
                    messageClassName
                  )}
                >
                  {line.message}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      <Show if={canToggle}>
        <div>
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="ml-auto flex items-center gap-1 text-xs font-medium text-content-200 hover:text-content-400 transition-colors"
          >
            <span>{expanded ? "Show less" : "Show more"}</span>
            <HugeiconsIcon
              icon={expanded ? ArrowUp01Icon : ArrowDown01Icon}
              size={14}
            />
          </button>
        </div>
      </Show>
    </>
  );
};
